import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { Autocomplete, Box, Chip, Typography } from '@mui/material'
import { Business as BusinessIcon, Add as AddIcon } from '@mui/icons-material'
import convertLeadLogo from '../../assests/drawerIcons/convertleadLogo.svg'
import { convertLeadSchema, type ConvertLeadFormData } from '../../lib/schemas/convertLeadSchema'
import { LEAD_SOURCES, SOURCE_LABELS } from '../../lib/api/leadsApi'
import { CustomerType } from '../../lib/api/customerTypesApi'
import { dealsApi, DEAL_PRIORITIES, DEAL_PRIORITY_LABELS } from '../../lib/api/dealsApi'
import { customersApi } from '../../lib/api/customersApi'
import { themeColors } from '../../app/layout/theme'
import { FormInput } from '../ui'
import {
  EntityFormDrawer,
  EntityFormSection,
  EntityDrawerInput,
  EntityDrawerSelect,
  EntityFormField,
  entityDrawerInputSx,
} from '../shared/EntityFormDrawer'

const SOURCE_OPTIONS = LEAD_SOURCES.map(s => ({
  label: SOURCE_LABELS[s] ?? s,
  value: s,
}))

const PRIORITY_OPTIONS = DEAL_PRIORITIES.map(p => ({
  label: DEAL_PRIORITY_LABELS[p] ?? p,
  value: p,
}))

interface ConvertLeadDrawerProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ConvertLeadFormData) => void
  isSubmitting?: boolean
  /** Pre-fill contact/company from the lead being converted */
  initialData?: Partial<ConvertLeadFormData>
  /** Available companies to link */
  companyOptions?: { label: string; value: string }[]
  /** Available contacts to link */
  contactOptions?: { label: string; value: string }[]
  orgId?: string | null
  customerTypes?: CustomerType[]
}

export function ConvertLeadDrawer({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialData,
  companyOptions = [],
  contactOptions = [],
  orgId,
  customerTypes = [],
}: ConvertLeadDrawerProps) {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  const todayStr = `${yyyy}-${mm}-${dd}`

  const { handleSubmit, watch, setValue, formState: { errors } } = useForm<ConvertLeadFormData>({
    resolver: zodResolver(convertLeadSchema),
    values: {
      dealName: '', dealStage: '', expectedClosingDate: '',
      amount: '', company: '', contact: '', owner: '', description: '',
      source: '', priority: 'MEDIUM', customerTypeId: undefined,
      ...initialData,
    },
  })

  const [companySearch, setCompanySearch] = useState('')

  const { data: companySuggestions } = useQuery({
    queryKey: ['company-suggestions', companySearch],
    queryFn: () => customersApi.getAll({ companyNameSearch: companySearch, pageSize: 5 }),
    enabled: companySearch.length > 1
  })

  const { data: stages = [] } = useQuery({
    queryKey: ['pipeline-stages', orgId],
    queryFn: () => dealsApi.getStages(undefined, orgId || undefined),
    staleTime: 5 * 60_000,
  })

  const stageOptions = stages.map(s => ({ label: s.name, value: s.name }))

  useEffect(() => {
    if (stageOptions.length > 0 && !watch('dealStage')) {
      setValue('dealStage', stageOptions[0].value)
    }
  }, [stageOptions, setValue, watch])

  const f = (name: keyof ConvertLeadFormData) => (val: string) => setValue(name, val, { shouldValidate: true })

  return (
    <EntityFormDrawer
      open={open}
      title="Convert Lead"
      icon={<img src={convertLeadLogo} alt="convert lead" style={{ width: 20, height: 20 }} />}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Convert to Deal"
      isSubmitting={isSubmitting}
    >
      <EntityFormSection>
        <EntityDrawerInput label="Deal name" required value={watch('dealName')} onChange={f('dealName')} placeholder="Enter deal name" error={errors.dealName?.message}/>
        <EntityDrawerSelect label="Deal Stage" required value={watch('dealStage')} onChange={f('dealStage')} options={stageOptions} placeholder="Select Stage type" error={errors.dealStage?.message} />
        <EntityDrawerInput label="Expected Closing Date" required value={watch('expectedClosingDate')} onChange={f('expectedClosingDate')} type="date" inputProps={{ min: todayStr }} error={errors.expectedClosingDate?.message} />
        <EntityDrawerInput label="Amount" required inputProps={{ min: 1 }} value={watch('amount') ?? ''} onChange={f('amount')} placeholder="Enter Amount" type="number" error={errors.amount?.message} />
        
        {initialData?.company ? (
          <EntityDrawerSelect
            label="Company"
            required
            value={watch('company') ?? ''}
            onChange={f('company')}
            options={companyOptions}
            placeholder="Company Name"
            disabled={true}
          />
        ) : (
          <EntityFormField label="Company" required>
            <Autocomplete
              freeSolo
              options={companySuggestions?.items || []}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option
                return option.companyName || option.name || ''
              }}
              value={watch('company') || ''}
              onChange={(_e, val) => {
                if (typeof val === 'string') {
                  f('company')(val)
                } else if (val) {
                  const name = val.companyName || val.name || ''
                  f('company')(name)
                }
              }}
              onInputChange={(_e, val) => {
                setCompanySearch(val)
                f('company')(val)
              }}
              renderInput={(params) => (
                <FormInput
                  {...params}
                  placeholder="Search or enter company name"
                  error={!!errors.company}
                  helperText={errors.company?.message}
                  sx={entityDrawerInputSx}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1, px: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                      {option.companyName || option.name}
                    </Typography>
                    <Chip
                      label="Exists"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '10px',
                        fontWeight: 700,
                        bgcolor: '#fee2e2',
                        color: '#ef4444',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 3.25 }}>
                    {option.phone} • {option.city || 'No City'}
                  </Typography>
                </Box>
              )}
              noOptionsText={
                companySearch.length > 1 ? (
                  <Box sx={{ py: 1, px: 2, cursor: 'pointer', color: themeColors.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>Create "{companySearch}"</Typography>
                  </Box>
                ) : "Type to search..."
              }
            />
          </EntityFormField>
        )}

        <EntityDrawerSelect label="Contact" value={watch('contact') ?? ''} onChange={f('contact')} options={contactOptions} placeholder="70658265252" disabled={!!initialData?.contact} />
        <EntityDrawerSelect label="Source" value={watch('source') ?? ''} onChange={f('source')} options={SOURCE_OPTIONS} placeholder="Select Source" disabled={!!initialData?.source} />
        <EntityDrawerSelect
          label="Customer Type"
          required
          value={watch('customerTypeId') != null ? String(watch('customerTypeId')) : ''}
          onChange={(v) => setValue('customerTypeId', v ? Number(v) : undefined, { shouldValidate: true })}
          options={customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) }))}
          placeholder="Select customer type"
          error={errors.customerTypeId?.message}
        />
        <EntityDrawerSelect label="Priority" value={watch('priority') ?? ''} onChange={f('priority')} options={PRIORITY_OPTIONS} placeholder="Select Priority" error={errors.priority?.message} />
        <EntityDrawerInput label="Owner" required value={watch('owner') ?? ''} onChange={f('owner')} placeholder="Select Owner" fullWidth disabled />
        <EntityDrawerInput label="Description" value={watch('description') ?? ''} onChange={f('description')} placeholder="Type here" multiline minRows={4} fullWidth />
      </EntityFormSection>
    </EntityFormDrawer>
  )
}
